import { RowMenu } from '../../components/row-menu';
import type { StatOption } from '../../lib/api-types';
import { FormationGlyph } from '../play-entry/formation-glyphs';

import { DEFAULT_PERSONNEL_COPY, personnelOptionText } from './default-personnel';
import { CONCEPT_DESIGN_LABELS, readDesignType } from './design-type';

export interface OptionRowProps {
  option: StatOption;
  canWrite: boolean;
  isBusy: boolean;
  isFirst: boolean;
  isLast: boolean;
  canReorder: boolean;
  showGlyph: boolean;
  /** Offensive concepts: show the Run / Pass / RPO badge (nothing when unset). */
  showDesign?: boolean;
  /** Formations: the personnel group this formation pre-fills on Play Entry (badge). */
  defaultPersonnel?: StatOption | null;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (delta: -1 | 1) => void;
}

/**
 * One option in the Playbook list, on one ~36px line: switch, label (+ design
 * pill, stored code, notes), Stock/Team pill, usage, then compact actions —
 * move up/down (manual order only), Edit, and a ⋯ menu holding Disable/Delete.
 */
export function OptionRow(p: OptionRowProps): JSX.Element {
  const { option } = p;
  const switchId = `option-${option.option_id}-enabled`;
  const design = p.showDesign === true ? readDesignType(option.design_type) : null;
  return (
    <li
      className={`playbook-option${option.is_enabled ? '' : ' is-disabled'}`}
      data-option={option.value}
      data-enabled={option.is_enabled ? 'true' : 'false'}
      data-stock={option.is_stock ? 'true' : 'false'}
      data-design={design ?? undefined}
      data-personnel={p.defaultPersonnel?.value ?? undefined}
    >
      <label className="playbook-option__switch" htmlFor={switchId}>
        <input
          id={switchId}
          type="checkbox"
          role="switch"
          checked={option.is_enabled}
          disabled={!p.canWrite || p.isBusy}
          aria-label={`${option.label} enabled on Play Entry`}
          onChange={p.onToggle}
        />
        <span className="playbook-option__track" aria-hidden="true" />
      </label>
      {p.showGlyph && option.glyph_svg ? (
        <span className="playbook-option__glyph">
          <FormationGlyph glyphSvg={option.glyph_svg} label={option.label} />
        </span>
      ) : null}
      <div className="playbook-option__text">
        <span className="playbook-option__label">{option.label}</span>
        {design !== null ? (
          <span
            className={`playbook-option__design playbook-option__design--${design}`}
            data-testid="playbook-design-badge"
            title={`Designed as ${CONCEPT_DESIGN_LABELS[design]}`}
          >
            {CONCEPT_DESIGN_LABELS[design]}
          </span>
        ) : null}
        {p.defaultPersonnel ? (
          <span
            className="playbook-option__personnel"
            data-testid="playbook-personnel-badge"
            title={DEFAULT_PERSONNEL_COPY.badgeTitle(personnelOptionText(p.defaultPersonnel))}
          >
            {DEFAULT_PERSONNEL_COPY.badge(p.defaultPersonnel.value)}
          </span>
        ) : null}
        <code className="playbook-option__code" title="Stored value">
          {option.value}
        </code>
        {option.notes ? (
          <span className="playbook-option__notes muted" title={option.notes}>
            {option.notes}
          </span>
        ) : null}
      </div>
      <span
        className={`playbook-option__badge playbook-option__badge--${option.is_stock ? 'stock' : 'team'}`}
      >
        {option.is_stock ? 'Stock' : 'Team'}
      </span>
      <span className="playbook-option__usage" title="Plays this season using this option">
        {option.usage_count > 0
          ? `${option.usage_count} play${option.usage_count === 1 ? '' : 's'}`
          : null}
      </span>
      {p.canWrite ? (
        <div className="playbook-option__actions">
          <button
            type="button"
            className="playbook-option__icon"
            aria-label={`Move ${option.label} up`}
            title={p.canReorder ? 'Move up' : 'Reorder in Manual order with no search or filter'}
            disabled={p.isFirst || !p.canReorder}
            onClick={() => p.onMove(-1)}
          >
            <span aria-hidden="true">↑</span>
          </button>
          <button
            type="button"
            className="playbook-option__icon"
            aria-label={`Move ${option.label} down`}
            title={p.canReorder ? 'Move down' : 'Reorder in Manual order with no search or filter'}
            disabled={p.isLast || !p.canReorder}
            onClick={() => p.onMove(1)}
          >
            <span aria-hidden="true">↓</span>
          </button>
          <button
            type="button"
            className="playbook-option__icon"
            title={`Edit ${option.label}`}
            onClick={p.onEdit}
            disabled={p.isBusy}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
              <path
                d="M11.3 2.3a1 1 0 0 1 1.4 0l1 1a1 1 0 0 1 0 1.4l-8 8L3 13l.3-2.7z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
            </svg>
            <span className="visually-hidden">Edit</span>
          </button>
          <RowMenu
            label={`More actions for ${option.label}`}
            items={[
              {
                id: 'remove',
                label: option.is_stock ? 'Disable' : 'Delete',
                tone: 'danger',
                isDisabled: p.isBusy,
                onSelect: p.onDelete,
              },
            ]}
          />
        </div>
      ) : null}
    </li>
  );
}
